/**
 * Section registry - the single source of truth for the app's sections, their
 * accent colours, icons, labels, routes, and sidebar grouping. Consumed by the
 * sidebar, mobile nav, PageHeader, SectionTabs, StatTile, SectionCard, etc.
 */
import type { ComponentType } from 'react';
import {
  Banknote,
  BookOpen,
  Calendar,
  LayoutDashboard,
  Settings2,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';

export type SectionKey =
  | 'dashboard'
  | 'batches'
  | 'ledgers'
  | 'cash'
  | 'match'
  | 'people'
  | 'accounts'
  | 'settings';

export type SectionGroup = 'Overview' | 'Daily' | 'Setup';

export interface SectionDef {
  readonly key: SectionKey;
  readonly label: string;
  readonly to: string;
  readonly group: SectionGroup;
  readonly icon: ComponentType<{ className?: string; strokeWidth?: number | string }>;
  readonly color: string;
  readonly tint: string;
  readonly canvas: string;
  readonly surface: string;
  readonly plate: string;
  readonly dock: string;
  readonly edge: string;
  readonly primaryTab: boolean;
  readonly mobileHint: string;
}

function def(
  key: SectionKey,
  label: string,
  to: string,
  group: SectionGroup,
  icon: SectionDef['icon'],
  primaryTab: boolean,
  mobileHint: string,
): SectionDef {
  return {
    key,
    label,
    to,
    group,
    icon,
    color: `var(--sec-${key})`,
    tint: `var(--sec-${key}-tint)`,
    canvas: `var(--sec-${key}-canvas)`,
    surface: `var(--sec-${key}-surface)`,
    plate: `var(--sec-${key}-plate)`,
    dock: `var(--sec-${key}-dock)`,
    edge: `var(--sec-${key}-edge)`,
    primaryTab,
    mobileHint,
  };
}

export const SECTIONS: Record<SectionKey, SectionDef> = {
  dashboard: def('dashboard', 'Home', '/', 'Overview', LayoutDashboard, true, 'Today, balances, and recent work'),
  batches: def('batches', 'Batches', '/batches', 'Daily', Calendar, true, 'Weekly import, verify, and posting queue'),
  ledgers: def('ledgers', 'Ledgers', '/ledgers', 'Daily', BookOpen, true, 'Party balances and running statements'),
  cash: def('cash', 'Cash', '/cash', 'Daily', Banknote, true, 'Direct people cashbook and movement'),
  match: def('match', 'Match', '/match', 'Daily', Zap, true, 'Quick split posting and routing rules'),
  people: def('people', 'People', '/people', 'Setup', Users, false, 'Clients and uplines'),
  accounts: def('accounts', 'Accounts', '/accounts', 'Setup', Wallet, false, 'Accounts, rates, and assignments'),
  settings: def('settings', 'Settings', '/settings', 'Setup', Settings2, false, 'Users, repair tools, and preferences'),
};

export const SECTION_GROUPS: ReadonlyArray<{ label: SectionGroup; items: SectionDef[] }> = [
  { label: 'Overview', items: [SECTIONS.dashboard] },
  { label: 'Daily', items: [SECTIONS.batches, SECTIONS.ledgers, SECTIONS.cash, SECTIONS.match] },
  { label: 'Setup', items: [SECTIONS.people, SECTIONS.accounts, SECTIONS.settings] },
];

export const SECTION_LIST: ReadonlyArray<SectionDef> = SECTION_GROUPS.flatMap((g) => g.items);

export function sectionOf(pathname: string): SectionKey {
  if (pathname.startsWith('/batches')) return 'batches';
  if (pathname.startsWith('/ledgers') || pathname.startsWith('/parties')) return 'ledgers';
  if (pathname.startsWith('/cash')) return 'cash';
  if (pathname.startsWith('/match')) return 'match';
  if (pathname.startsWith('/people')) return 'people';
  if (pathname.startsWith('/accounts')) return 'accounts';
  if (pathname.startsWith('/wizard')) return 'accounts';
  if (
    pathname.startsWith('/settings') ||
    pathname.startsWith('/account') ||
    pathname.startsWith('/repair') ||
    pathname.startsWith('/fix-problems')
  ) {
    return 'settings';
  }
  return 'dashboard';
}

export function sectionAccent(key: SectionKey): { color: string; tint: string } {
  const s = SECTIONS[key];
  return { color: s.color, tint: s.tint };
}
